import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Joi from 'joi';
import isEqual from 'lodash/isEqual';
import clsx from 'clsx';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';

import { useGetTunnelMessagesQuery } from 'api/main';
import { userSelectors, tunnelActions, tunnelSelectors } from 'reducers';
import { setTunnelChangesHandlers, validateSchema } from 'utils/helpers';
import { fundTypes, RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import TunnelBox from '../common/TunnelBox';
import TunnelStep from '../common/TunnelStep';
import {
    CustomTextField,
    ErrorMessage,
    SubmitButton,
    InfoButton,
    CustomFormLabel,
    CustomDatePicker,
    RadioLabel,
} from 'components';
import {
    Box,
    FormControl,
    FormControlLabel,
    InputAdornment,
    RadioGroup,
    Typography,
    FormGroup,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { initDistributorKYC } from 'schemas/tunnel';

const useStyles = makeStyles(theme => ({
    button: {
        marginRight: 20,
    },
    infoButton: {
        marginLeft: theme.spacing(2),
    },
    flexContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'column',
    },
    container: {
        width: '100%',
        overflow: 'auto',
        padding: theme.spacing(2, 3),
        marginBottom: theme.spacing(2),
    },
    label: {
        display: 'block',
        flex: '1 0',
        fontSize: 16,
        marginBottom: theme.spacing(),
        letterSpacing: 1.6,
        color: theme.palette.grey[600],
    },
    inputField: {
        margin: '0 10px 0 0',
    },
    radioOption: {
        fontSize: 16,
        color: theme.palette.grey[600],
    },
    option: {
        alignItems: 'flex-start',
    },
    field: { width: '100%', maxWidth: theme.constants.tunnelInputWidth },
    percentageField: {
        width: 80,
    },
    formControlLabel: {
        color: theme.palette.grey[600],
        cursor: 'initial',
        margin: '0 0 16px 0',
        alignItems: 'center',
    },
    textarea: {
        height: 'auto',
        '& > textarea': {
            height: 'auto',
        },
    },
    radioOptions: {
        color: theme.palette.grey[600],
    },
}));

const DistributorKYC = props => {
    const { userStep, roleName, handleNextStep } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const buttonLoader = useSelector(tunnelSelectors.getButtonLoader);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const tunnelErrors = useSelector(tunnelSelectors.getTunnelErrors);
    const { data: messages } = useGetTunnelMessagesQuery();

    const isStepVerified = useMemo(() => !!userStep?.successfulAttemptsNumber, [userStep]);
    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const distributorCompany = useMemo(
        () => tunnelSubscription?.distributorCompany,
        [tunnelSubscription]
    );
    const isFPS = useMemo(
        () => tunnelSubscription?.investmentFund?.type === fundTypes.FPS,
        [tunnelSubscription]
    );
    const userIsAdminDistibutor = roleName === RoleNames.ADMIN_CIF;
    const userIsPSI = roleName === RoleNames.PSI;

    const defaultForm = useMemo(() => initDistributorKYC(distributorCompany), [distributorCompany]);
    const [form, setForm] = useState(defaultForm);
    const schema = useMemo(
        () => ({
            privateEquityAssetManagementPercentage: Joi.number()
                .empty('')
                .min(0)
                .max(100)
                .messages({
                    'number.base': texts.PERCENTAGE_VALUE,
                    'number.min': texts.PERCENTAGE_VALUE,
                    'number.max': texts.PERCENTAGE_VALUE,
                }),
            professionalClientPercentage: Joi.number().empty('').min(0).max(100).messages({
                'number.base': texts.PERCENTAGE_VALUE,
                'number.min': texts.PERCENTAGE_VALUE,
                'number.max': texts.PERCENTAGE_VALUE,
            }),
            informedClientPercentage: Joi.number().empty('').min(0).max(100).messages({
                'number.base': texts.PERCENTAGE_VALUE,
                'number.min': texts.PERCENTAGE_VALUE,
                'number.max': texts.PERCENTAGE_VALUE,
            }),
            nonProfessionalClientPercentage: Joi.number().empty('').min(0).max(100).messages({
                'number.base': texts.PERCENTAGE_VALUE,
                'number.min': texts.PERCENTAGE_VALUE,
                'number.max': texts.PERCENTAGE_VALUE,
            }),
            rcsRegistrationAddress: Joi.string()
                .trim()
                .pattern(/^[ a-zA-ZÀ-ÿ]+$/)
                .required()
                .messages({
                    'string.empty': texts.SAISIR_LE_CHAMP,
                    'string.pattern.base': texts.RCS_REGISTRATION_ADDRESS_ONLY_LETTERS,
                }),
            oriasNumber: userIsAdminDistibutor
                ? Joi.string().length(8).pattern(/^\d+$/).required().messages({
                      'string.empty': texts.SAISIR_LE_CHAMP,
                      'string.length': texts.ORIAS_NUMBER_LENGTH,
                      'string.pattern.base': texts.ORIAS_NUMBER_LENGTH,
                  })
                : Joi.any(),
            acprNumber: userIsPSI
                ? Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP })
                : Joi.any(),
            rcsNumber: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            vatNumber: Joi.string().required().messages({ 'string.empty': texts.SAISIR_LE_CHAMP }),
            distributionWays: Joi.string()
                .allow('')
                .max(250)
                .messages({ 'string.max': texts.TEXT_TOO_LONG() }),
            categoryCommercialization: Joi.string().required().messages({
                'string.empty': texts.REMUNERATION_METHOD_ERROR,
            }),
        }),
        [userIsAdminDistibutor, userIsPSI]
    );
    const [errors, setErrors] = useState({ ...Object.keys(defaultForm) });

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setForm(oldValue => ({ ...oldValue, [key]: value }));
        },
        []
    );

    const handleDateChange = useCallback(
        newDate =>
            setForm(oldValue => ({
                ...oldValue,
                rcsRegistrationDate: newDate,
                registrationDate: newDate ? newDate.format('L') : '',
            })),
        []
    );

    const clientTypePercentageInfo = useMemo(() => {
        const proClientPer = +form.professionalClientPercentage;
        const informedClientPer = +form.informedClientPercentage;
        const nonProClientPer = +form.nonProfessionalClientPercentage;
        const allFieldsEmpty =
            proClientPer === 0 && informedClientPer === 0 && nonProClientPer === 0;
        return {
            allFieldsEmpty,
            total: proClientPer + informedClientPer + nonProClientPer,
            payload: {
                professionalClientPercentage: !allFieldsEmpty ? proClientPer : null,
                informedClientPercentage: !allFieldsEmpty ? informedClientPer : null,
                nonProfessionalClientPercentage: !allFieldsEmpty ? nonProClientPer : null,
            },
        };
    }, [form]);

    const isFormValid = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        //if at least one value is not 0 then we need to check if the sum of all three is equal to 100
        if (!clientTypePercentageInfo.allFieldsEmpty) {
            if (clientTypePercentageInfo.total !== 100) {
                setErrors({ ...newErrors, totalPercent: texts.TOTAL_DES_POURCENTAGES });
                return false;
            }
        }

        if (!form.registrationDate) {
            setErrors(prevState => ({
                ...prevState,
                registrationDate: texts.SAISIR_LE_CHAMP,
            }));
            return false;
        }

        if (!form.rcsRegistrationDate?.isBefore(moment(), 'd')) {
            setErrors(prevState => ({
                ...prevState,
                registrationDate: texts.FUTURE_DATE_VALIDATION,
            }));
            return false;
        }

        return valid;
    }, [clientTypePercentageInfo, form, schema]);

    const handleSubmit = useCallback(
        afterSubmitCallback => () => {
            const {
                professionalClientPercentage,
                informedClientPercentage,
                nonProfessionalClientPercentage,
                ...formWithoutClientTypePercentages
            } = form;

            const payload = {
                ...formWithoutClientTypePercentages,
                ...clientTypePercentageInfo.payload,
            };
            if (isFormValid()) {
                dispatch(
                    tunnelActions.sendDistributorKYC(
                        payload,
                        afterSubmitCallback ? afterSubmitCallback : () => handleNextStep(true)
                    )
                );
            }
        },
        [dispatch, handleNextStep, isFormValid, form, clientTypePercentageInfo]
    );

    const allowSubmit = useMemo(
        () =>
            !!form.categoryCommercialization &&
            form.registrationDate &&
            form.rcsRegistrationAddress &&
            form.rcsNumber &&
            form.vatNumber &&
            (userIsAdminDistibutor ? form.oriasNumber : true) &&
            (userIsPSI ? form.acprNumber : true),
        [form, userIsAdminDistibutor, userIsPSI]
    );

    // this function returns true if the step has suffered changes and false otherwise
    const checkChanges = useCallback(() => !isEqual(defaultForm, form), [form, defaultForm]);

    const changesMade = useMemo(() => checkChanges(), [checkChanges]);

    useEffect(
        () => setTunnelChangesHandlers(handleSubmit, checkChanges, isResubmit),
        [checkChanges, handleSubmit, isResubmit]
    );

    const proClientLabel = (
        <Box display="flex" alignItems="center" flexWrap="nowrap">
            {texts.PRO_CLIENTS_PERCENTAGE}
            <InfoButton
                disableMarginBottom
                title={texts.DEFINITION}
                content={
                    <React.Fragment>
                        <Typography gutterBottom>{texts.PRO_CLIENTS_INFO}</Typography>
                        <ul>
                            <li>
                                <Typography gutterBottom>
                                    {texts.PRO_CLIENTS_INFO_OPTION_1}
                                </Typography>
                            </li>
                            <li>
                                <Typography gutterBottom>
                                    {texts.PRO_CLIENTS_INFO_OPTION_2}
                                </Typography>
                                <ul>
                                    <li>
                                        <Typography gutterBottom>
                                            {texts.PRO_CLIENTS_INFO_OPTION_2_1}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography gutterBottom>
                                            {texts.PRO_CLIENTS_INFO_OPTION_2_2}
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography gutterBottom>
                                            {texts.PRO_CLIENTS_INFO_OPTION_2_3}
                                        </Typography>
                                    </li>
                                </ul>
                            </li>
                            <li>
                                <Typography gutterBottom>
                                    {texts.PRO_CLIENTS_INFO_OPTION_3}
                                </Typography>
                                <Box>
                                    <Typography gutterBottom>
                                        {texts.PRO_CLIENTS_INFO_OPTION_3_BECOME_PRO}
                                    </Typography>
                                    <ul>
                                        <li>
                                            <Typography gutterBottom>
                                                {texts.PRO_CLIENTS_INFO_OPTION_3_1}
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography gutterBottom>
                                                {texts.PRO_CLIENTS_INFO_OPTION_3_2}
                                            </Typography>
                                        </li>
                                        <li>
                                            <Typography gutterBottom>
                                                {texts.PRO_CLIENTS_INFO_OPTION_3_3}
                                            </Typography>
                                        </li>
                                    </ul>
                                </Box>
                            </li>
                        </ul>
                    </React.Fragment>
                }
            />
        </Box>
    );

    return (
        <TunnelStep title={texts.MIEUX_VOUS_CONNAITRE}>
            <TunnelBox>
                <CustomTextField
                    inputStyle={classes.field}
                    label={texts.SIREN_NUMBER}
                    value={form.rcsNumber}
                    onChange={handleChange('rcsNumber')}
                    error={errors.rcsNumber || tunnelErrors.rcsNumber}
                    showError={!tunnelErrors.rcsNumber}
                />
                <CustomTextField
                    inputStyle={classes.field}
                    label={texts.RCS_ADDRESS}
                    value={form.rcsRegistrationAddress}
                    onChange={handleChange('rcsRegistrationAddress')}
                    error={errors.rcsRegistrationAddress || tunnelErrors.rcsRegistrationAddress}
                    showError={!tunnelErrors.rcsRegistrationAddress}
                />
                <Box display="flex" position="relative" alignItems="flex-end">
                    <CustomDatePicker
                        disableFuture
                        inputStyle={classes.field}
                        label={texts.RCS_DATE}
                        value={form.rcsRegistrationDate}
                        onChange={handleDateChange}
                        error={errors.registrationDate}
                        showError={!!errors.registrationDate}
                    />
                    <InfoButton
                        className={classes.infoButton}
                        title={texts.RCS_DATE}
                        content={texts.DATE_PICKER_INFO}
                    />
                </Box>
                <CustomTextField
                    inputStyle={classes.field}
                    label={texts.VAT_NUMBER}
                    value={form.vatNumber}
                    onChange={handleChange('vatNumber')}
                    error={errors.vatNumber}
                    showError={!tunnelErrors.vatNumber}
                />
                {userIsAdminDistibutor && (
                    <CustomTextField
                        type="number"
                        inputStyle={classes.field}
                        label={texts.ORIAS_NUMBER}
                        value={form.oriasNumber}
                        onChange={handleChange('oriasNumber')}
                        error={errors.oriasNumber}
                        showError={!tunnelErrors.oriasNumber}
                    />
                )}
                {userIsPSI && (
                    <CustomTextField
                        inputStyle={classes.field}
                        label={texts.ACPR_NUMBER}
                        value={form.acprNumber}
                        onChange={handleChange('acprNumber')}
                    />
                )}
                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        className={classes.flexContainer}
                        value={form.categoryCommercialization}
                        onChange={handleChange('categoryCommercialization')}
                    >
                        <label className={classes.label}>{texts.REMUNERATION_METHOD}</label>
                        <RadioLabel
                            value="public"
                            label={texts.ACCEPT_REMUNERATION}
                            labelPlacement="end"
                        />
                        <RadioLabel
                            value="private"
                            label={texts.REJECT_REMUNERATION}
                            labelPlacement="end"
                        />
                        <RadioLabel value="both" label={texts.BOTH} labelPlacement="end" />
                    </RadioGroup>
                    <ErrorMessage message={errors.categoryCommercialization} />
                </FormControl>
            </TunnelBox>
            <TunnelBox>
                <Box mb={2}>
                    <CustomFormLabel>{texts.ASSET_MANAGEMENT_AMOUNT}</CustomFormLabel>
                    <RadioGroup
                        name="assetManagementAmount"
                        value={form.assetManagementAmount}
                        onChange={handleChange('assetManagementAmount')}
                    >
                        <RadioLabel
                            value="asset_management_amount_options.between_0_and_50ME"
                            label={messages?.['asset_management_amount_options.between_0_and_50ME']}
                            checked={
                                form.assetManagementAmount ===
                                'asset_management_amount_options.between_0_and_50ME'
                            }
                        />
                        <RadioLabel
                            value="asset_management_amount_options.between_50_and_100ME"
                            label={
                                messages?.['asset_management_amount_options.between_50_and_100ME']
                            }
                            checked={
                                form.assetManagementAmount ===
                                'asset_management_amount_options.between_50_and_100ME'
                            }
                        />
                        <RadioLabel
                            value="asset_management_amount_options.more_than_100ME"
                            label={messages?.['asset_management_amount_options.more_than_100ME']}
                            checked={
                                form.assetManagementAmount ===
                                'asset_management_amount_options.more_than_100ME'
                            }
                        />
                    </RadioGroup>
                </Box>
                <CustomTextField
                    formatNumber
                    className={classes.percentageField}
                    value={form.privateEquityAssetManagementPercentage}
                    onChange={handleChange('privateEquityAssetManagementPercentage')}
                    error={errors.privateEquityAssetManagementPercentage}
                    showError={!!errors.privateEquityAssetManagementPercentage}
                    label={texts.PRIVATE_EQUITY_ASSET_MANAGEMENT_AMOUNT}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <FormGroup className={classes.flexContainer}>
                    <label className={classes.label}>{texts.CLIENT_REPARTITION}</label>
                    <FormControlLabel
                        className={classes.formControlLabel}
                        control={
                            <CustomTextField
                                formatNumber
                                value={form.professionalClientPercentage}
                                inputStyle={clsx(classes.inputField, classes.percentageField)}
                                onChange={handleChange('professionalClientPercentage')}
                                error={errors.professionalClientPercentage}
                                showError={!!errors.professionalClientPercentage}
                                endAdornment={<InputAdornment position="start">%</InputAdornment>}
                            />
                        }
                        label={proClientLabel}
                    />
                    <FormControlLabel
                        className={classes.formControlLabel}
                        control={
                            <CustomTextField
                                formatNumber
                                value={form.informedClientPercentage}
                                inputStyle={clsx(classes.inputField, classes.percentageField)}
                                onChange={handleChange('informedClientPercentage')}
                                error={errors.informedClientPercentage}
                                showError={!!errors.informedClientPercentage}
                                endAdornment={<InputAdornment position="start">%</InputAdornment>}
                            />
                        }
                        label={
                            <Box display="flex" alignItems="center" flexWrap="nowrap">
                                {texts.INFORMED_CLIENTS_PERCENTAGE}
                                <InfoButton
                                    disableMarginBottom
                                    title={texts.DEFINITION}
                                    content={
                                        <React.Fragment>
                                            <Typography gutterBottom>
                                                {texts.INFORMED_CLIENTS_INFO_1}
                                            </Typography>
                                            <Typography gutterBottom>
                                                {isFPS
                                                    ? texts.INFORMED_CLIENTS_INFO_2_FPS
                                                    : texts.INFORMED_CLIENTS_INFO_2}
                                            </Typography>
                                        </React.Fragment>
                                    }
                                />
                            </Box>
                        }
                    />
                    <FormControlLabel
                        className={classes.formControlLabel}
                        control={
                            <CustomTextField
                                formatNumber
                                value={form.nonProfessionalClientPercentage}
                                inputStyle={clsx(classes.inputField, classes.percentageField)}
                                onChange={handleChange('nonProfessionalClientPercentage')}
                                error={errors.informedClientPercentage}
                                showError={!!errors.informedClientPercentage}
                                endAdornment={<InputAdornment position="start">%</InputAdornment>}
                            />
                        }
                        label={
                            <Box display="flex" alignItems="center" flexWrap="nowrap">
                                {texts.NON_PRO_CLIENTS_PERCENTAGE}
                                <InfoButton
                                    disableMarginBottom
                                    title={texts.DEFINITION}
                                    content={texts.NON_PRO_CLIENTS_INFO}
                                />
                            </Box>
                        }
                    />
                    <ErrorMessage message={errors.totalPercent} />
                </FormGroup>
                <CustomTextField
                    multiline
                    rows={3}
                    className={clsx(classes.textarea, classes.field)}
                    label={texts.DISTRIBUTION_WAYS}
                    value={form.distributionWays}
                    onChange={handleChange('distributionWays')}
                    error={errors.distributionWays}
                />
                <CustomTextField
                    formatNumber
                    className={classes.field}
                    value={form.averagePrivateEquityTicketAmount}
                    onChange={handleChange('averagePrivateEquityTicketAmount')}
                    error={errors.assetManagementAmount}
                    showError={false}
                    label={texts.AVERAGE_PRIVATE_EQUITY_TICKET_AMOUNT}
                />
            </TunnelBox>
            <SubmitButton
                text={isStepVerified ? texts.SAVE_AND_CONTINUE : texts.SUIVANT}
                onClick={!changesMade && isStepVerified ? handleNextStep : handleSubmit()}
                disabled={buttonLoader || !allowSubmit}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

export default DistributorKYC;
